import { httpClient } from '@/configs/axios.config'
import type {
  DealDecisionOptions,
  DealLeadReanalysis,
  DealIntakeActivityList,
  DealIntakeMessageIssue,
  DealIntakeMessageList,
  DealIntakePollResult,
  DealIntakePollStatusList,
  DealLeadCounts,
  DealLeadDetail,
  DealLeadFieldOptions,
  DealLeadList,
  DealLeadSearch,
  CorrectDealLead,
  SaveDealLeadNote,
  ArchiveDealLead,
  RecordDealDecision,
  SendToDealCloud,
} from '@/types/api'

// Inbox and History are this one search split by disposition; don't add a History endpoint.
export async function searchDealLeads(
  params: DealLeadSearch
): Promise<DealLeadList> {
  const response = await httpClient.post<DealLeadList>(
    '/deal-leads/search',
    params
  )
  return response.data
}

export async function getDealLeadCounts(): Promise<DealLeadCounts> {
  const response = await httpClient.get<DealLeadCounts>('/deal-leads/counts')
  return response.data
}

// Separate from leads: a failing poll yields no leads, which looks like a quiet week.
export async function getIntakePollStatus(): Promise<DealIntakePollStatusList> {
  const response = await httpClient.get<DealIntakePollStatusList>(
    '/deal-intake-messages/poll-status'
  )
  return response.data
}

// Only capture runs in-request; extraction continues on the worker.
export async function pollIntakeNow(): Promise<DealIntakePollResult> {
  const response = await httpClient.post<DealIntakePollResult>(
    '/deal-intake-messages/poll',
    undefined,
    // The panel reports every outcome itself; the generic toast would duplicate it.
    { skipErrorToast: true }
  )
  return response.data
}

export async function getIntakeActivity(): Promise<DealIntakeActivityList> {
  const response = await httpClient.get<DealIntakeActivityList>(
    '/deal-intake-messages/activity'
  )
  return response.data
}

export async function searchIntakeIssues(): Promise<DealIntakeMessageList> {
  const response = await httpClient.post<DealIntakeMessageList>(
    '/deal-intake-messages/search',
    {
      onlyIssues: true,
      limit: 50,
      sorts: [{ field: 'receivedAt', direction: 'desc' }],
    }
  )
  return response.data
}

export async function retryIntakeMessage(
  id: string
): Promise<DealIntakeMessageIssue> {
  const response = await httpClient.post<DealIntakeMessageIssue>(
    `/deal-intake-messages/${id}/retry`
  )
  return response.data
}

export async function rescreenDealLead(
  id: string
): Promise<DealLeadReanalysis> {
  const response = await httpClient.post<DealLeadReanalysis>(
    `/deal-leads/${id}/rescreen`
  )
  return response.data
}

// `discardCorrections` defaults off: a fresh extraction overwrites reviewer corrections.
export async function rereadDealLead(
  id: string,
  discardCorrections = false
): Promise<DealLeadReanalysis> {
  const response = await httpClient.post<DealLeadReanalysis>(
    `/deal-leads/${id}/reread`,
    { discardCorrections }
  )
  return response.data
}

export async function getDealLead(id: string): Promise<DealLeadDetail> {
  const response = await httpClient.get<DealLeadDetail>(`/deal-leads/${id}`)
  return response.data
}

export async function getDealLeadFieldOptions(): Promise<DealLeadFieldOptions> {
  const response = await httpClient.get<DealLeadFieldOptions>(
    '/deal-leads/field-options'
  )
  return response.data
}

export async function getDealDecisionOptions(): Promise<DealDecisionOptions> {
  const response = await httpClient.get<DealDecisionOptions>(
    '/deal-leads/decision-options'
  )
  return response.data
}

export async function recordDealDecision(
  id: string,
  body: RecordDealDecision
): Promise<DealLeadDetail> {
  const response = await httpClient.post<DealLeadDetail>(
    `/deal-leads/${id}/decision`,
    body
  )
  return response.data
}

// Own route: POST /decision requires a disposition and would append a review.
export async function saveDealLeadNote(
  id: string,
  body: SaveDealLeadNote
): Promise<DealLeadDetail> {
  const response = await httpClient.patch<DealLeadDetail>(
    `/deal-leads/${id}/note`,
    body
  )
  return response.data
}

// Not the decision route: archiving isn't a judgement and must stay out of reviewer-vs-model stats.
export async function setDealLeadArchived(
  id: string,
  body: ArchiveDealLead
): Promise<DealLeadDetail> {
  const response = await httpClient.patch<DealLeadDetail>(
    `/deal-leads/${id}/archive`,
    body
  )
  return response.data
}

// Send the rendered version (stale saves are rejected) and only changed fields (they're logged).
export async function correctDealLead(
  id: string,
  body: CorrectDealLead
): Promise<DealLeadDetail> {
  const response = await httpClient.patch<DealLeadDetail>(
    `/deal-leads/${id}`,
    body
  )
  return response.data
}

export async function sendDealLeadToDealCloud(
  id: string,
  body: SendToDealCloud
): Promise<DealLeadDetail> {
  const response = await httpClient.post<DealLeadDetail>(
    `/deal-leads/${id}/send-to-dealcloud`,
    body
  )
  return response.data
}
